import { ReactNode, useMemo, useEffect } from 'react'
import { ThemeProvider as MuiThemeProvider, CssBaseline, createTheme } from '@mui/material'
import { useUIStore } from '@store/uiStore'
import { themeRegistry } from '@themes/registry'
import { useMyThemes } from '@hooks/useThemes'
import { buildThemeConfig } from '@themes/buildTheme'

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { themeName, themeMode } = useUIStore()
  const { data: serverThemes } = useMyThemes()

  // Register server themes into the registry when they arrive
  useEffect(() => {
    if (!serverThemes) return

    // Clear previous server themes and re-register from fresh response
    themeRegistry.clearServerThemes()

    for (const dto of serverThemes) {
      const config = buildThemeConfig(dto)
      themeRegistry.register(config)
    }

    // If the user's selected theme was revoked, fall back to adventure
    if (!themeRegistry.getTheme(themeName)) {
      useUIStore.getState().setTheme('adventure')
    }
  }, [serverThemes, themeName])

  const theme = useMemo(() => {
    // Get theme config from registry, fallback to adventure
    const themeConfig = themeRegistry.getTheme(themeName) || themeRegistry.getTheme('adventure')!

    // Select light or dark mode
    const themeOptions = themeMode === 'dark' ? themeConfig.dark : themeConfig.light

    // Create MUI theme
    return createTheme(themeOptions)
  }, [themeName, themeMode, serverThemes])

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}
